import * as React from 'react'
import { O, pipe, RA, RR, S, T, TE, t } from '@packages/prelude'
import * as domain from '@packages/domain'
import {
  getAllUserImageUrls,
  UserImageData,
  userImageImageLens,
  userImagePreviewUrlLens,
  userImageRemoteUrlLens
} from '~/components/ProductPreview/state/UserImageField'
import { bindFieldsByParameterId } from '~/components/ProductPreview/state/fieldParams'

export interface UseUserImageFieldsArgs {
  meta: domain.Meta.ProductPersonalization.Type
  parameters: ReadonlyArray<domain.IAParameter.Type>
  initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>
  // a restored image whose stored URL is gone is reported here (mirrors useAudioSegmentsState)
  onRestoreError: (error: domain.Error.Type) => void
}

export interface UseUserImageFieldsResult {
  userImage: RR.ReadonlyRecord<string, UserImageData>
  onImageUpload: (id: string) => (file: File) => void
  onImageRemove: (id: string) => () => void
  // Set the durable remote URL for the image bound to `paramId` (no-op for a non-image param).
  // Mirrors how AudioSegments receive their URL from a fresh preview — the locally-held File and
  // previewUrl are left intact (this only records the server-side asset for the cart DTO).
  setRemoteUrl: (paramId: number, url: string) => void
  reset: () => void
}

const isSupportedImageMime = (file: File): boolean =>
  file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'

// Fetch the restored image; a non-ok response means the asset is gone, not image bytes.
const fetchBlob = (url: string): TE.TaskEither<domain.Error.Type, Blob> =>
  pipe(
    TE.tryCatch(() => fetch(url), domain.Error.toUnknownError),
    TE.filterOrElse(
      r => r.ok,
      () => domain.Error.fromString(`failed to restore image from ${url}`)
    ),
    TE.chain(TE.tryCatchK(r => r.blob(), domain.Error.toUnknownError))
  )

// Reconstruct a File from the downloaded blob, deriving a filename + MIME from the URL when absent.
const fileFromBlob = (blob: Blob, remoteUrl: string): O.Option<File> =>
  pipe(
    O.Do,
    O.apS(
      'filename',
      pipe(
        O.tryCatch(() => new URL(remoteUrl).pathname.split('/')),
        O.chain(RA.last),
        O.alt(() => O.of('image.jpg'))
      )
    ),
    O.chain(({ filename }) =>
      pipe(
        blob.type,
        t.NonEmptyString.decode,
        O.fromEither,
        O.alt(() =>
          pipe(
            filename.split('.'),
            RA.last,
            O.map(ext => {
              switch (ext.toLowerCase()) {
                case 'png':
                  return 'image/png' as t.NonEmptyString
                case 'webp':
                  return 'image/webp' as t.NonEmptyString
                default:
                  return 'image/jpeg' as t.NonEmptyString
              }
            })
          )
        ),
        O.map(mimeType => new File([blob], filename, { type: mimeType }))
      )
    )
  )

// pers-bild (Image) fields bind by explicit parameterId to the UI param pool.
const pairImages = bindFieldsByParameterId('userImage', domain.IAParameter.ADT.is.UI)

// Restore one image field: no stored file → empty skeleton (normal); a stored URL that fails to
// fetch → log + report (asset gone). Curried on its effect deps so it stays pure and module-level.
const restore =
  (initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>, onRestoreError: (error: domain.Error.Type) => void) =>
  (field: domain.Meta.Fields.PersonalizationField.UserImage, param: domain.Parameter.UserImage.Type): T.Task<UserImageData> => {
    const base = {
      id: param.id,
      title: field.label,
      description: param.description,
      optional: param.optional,
      fieldId: field.id
    }
    const empty: UserImageData = { ...base, image: O.none, previewUrl: O.none, remoteUrl: O.none }

    return pipe(
      initialData,
      O.chain(d => domain.Api.IALoadPreviewResponse.fileById(String(param.id)).asFold().headOption(d)),
      O.match(
        () => T.of(empty),
        remoteUrl =>
          pipe(
            fetchBlob(remoteUrl),
            TE.match(
              error => {
                domain.Error.log(error)()
                onRestoreError(error)
                return empty
              },
              blob =>
                pipe(
                  fileFromBlob(blob, remoteUrl),
                  O.match(
                    () => empty,
                    image => ({
                      ...base,
                      image: O.some(image),
                      previewUrl: O.some(URL.createObjectURL(blob)),
                      remoteUrl: O.some(remoteUrl)
                    })
                  )
                )
            )
          )
      )
    )
  }

export const useUserImageFields = ({
  meta,
  parameters,
  initialData,
  onRestoreError
}: UseUserImageFieldsArgs): UseUserImageFieldsResult => {
  const [userImage, setUserImage] = React.useState<RR.ReadonlyRecord<string, UserImageData>>(RR.empty)

  React.useEffect(() => {
    pipe(
      pairImages(meta, parameters),
      RA.map(([field, param]) => restore(initialData, onRestoreError)(field, param)),
      T.sequenceArray,
      T.map(params => RR.fromFoldableMap(S.last<UserImageData>(), RA.Foldable)(params, p => [String(p.id), p])),
      T.map(setUserImage)
    )()
  }, [meta, parameters, initialData, onRestoreError])

  const onImageUpload = React.useCallback(
    (id: string) => (file: File) =>
      pipe(
        file,
        O.fromPredicate(isSupportedImageMime),
        O.matchW(
          () => domain.Error.log(domain.Error.fromString(`Unsupported image type: ${file.type}`))(),
          accepted =>
            setUserImage(prev => {
              pipe(prev, userImagePreviewUrlLens(id).getOption, O.flatten, O.map(URL.revokeObjectURL))
              return pipe(
                prev,
                userImageImageLens(id).set(O.some(accepted)),
                userImagePreviewUrlLens(id).set(O.some(URL.createObjectURL(accepted)))
              )
            })
        )
      ),
    []
  )

  const onImageRemove = React.useCallback(
    (id: string) => () =>
      setUserImage(prev => {
        pipe(prev, userImagePreviewUrlLens(id).getOption, O.flatten, O.map(URL.revokeObjectURL))
        return pipe(
          prev,
          userImageImageLens(id).set(O.none),
          userImagePreviewUrlLens(id).set(O.none),
          userImageRemoteUrlLens(id).set(O.none)
        )
      }),
    []
  )

  const setRemoteUrl = React.useCallback(
    (paramId: number, url: string) => setUserImage(userImageRemoteUrlLens(String(paramId)).set(O.some(url))),
    []
  )

  const reset = React.useCallback(() => {
    setUserImage(prev => {
      pipe(prev, getAllUserImageUrls, RA.map(URL.revokeObjectURL))
      return pipe(
        prev,
        RR.map(d => ({ ...d, image: O.none, previewUrl: O.none, remoteUrl: O.none }))
      )
    })
  }, [])

  return { userImage, onImageUpload, onImageRemove, setRemoteUrl, reset }
}
